import React, { useState, useEffect, useRef } from 'react';
import apiClient from '../../api/client';

// Global caches shared across all StructureImage instances
const svgCache = new Map<string, string>();
const pngBlobCache = new Map<number, string>(); // moleculeId -> blob URL

/** Invalidate cached structure image for a molecule (call after edit/save). */
export function invalidateStructureCache(moleculeId: number) {
  const url = pngBlobCache.get(moleculeId);
  if (url) {
    URL.revokeObjectURL(url);
    pngBlobCache.delete(moleculeId);
  }
}

interface StructureImageProps {
  smiles?: string;
  moleculeId?: number;
  size?: number;
  lazy?: boolean;
  updatedAt?: string; // ISO timestamp — change triggers cache-bust re-fetch
  className?: string;
  onClick?: () => void;
}

const StructureImage: React.FC<StructureImageProps> = ({ smiles, moleculeId, size = 80, lazy = false, updatedAt, className, onClick }) => {
  // Render at 2x the display size (retina) with a 128px floor so atom labels
  // stay proportional at small sizes — avoids the old fixed 200px floor that
  // forced large downscales (e.g. 200->36 = 5.5x) and washed out thin bonds.
  const renderSize = Math.max(size * 2, 128);
  const [svg, setSvg] = useState<string | null>(null);
  const [pngUrl, setPngUrl] = useState<string | null>(
    moleculeId ? pngBlobCache.get(moleculeId) ?? null : null
  );

  // When updatedAt changes, invalidate cached PNG to force re-fetch
  useEffect(() => {
    if (moleculeId && updatedAt) {
      invalidateStructureCache(moleculeId);
      setPngUrl(null);
      setPngFailed(false);
    }
  }, [moleculeId, updatedAt]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(!lazy);
  const [pngFailed, setPngFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazy loading via IntersectionObserver
  useEffect(() => {
    if (!lazy || !containerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [lazy]);

  // Use cached PNG when moleculeId is available
  const usePng = !!(moleculeId && !pngFailed);

  // Fetch cached PNG from /api/molecules/{id}/png
  useEffect(() => {
    if (!usePng || !visible || pngUrl || !moleculeId) { if (!usePng && !smiles) setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    setError(false);

    apiClient.get(`/molecules/${moleculeId}/png`, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return;
        const blob = new Blob([response.data], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        pngBlobCache.set(moleculeId, url);
        setPngUrl(url);
      })
      .catch(() => { if (!cancelled) setPngFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [usePng, visible, moleculeId, pngUrl]);

  // Fallback: SVG via Indigo render (when no moleculeId or PNG failed)
  const cacheKey = smiles ? `${smiles}|${renderSize}` : '';
  useEffect(() => {
    if (usePng || !smiles || !visible) { if (!usePng && !smiles) setLoading(false); return; }

    const cached = svgCache.get(cacheKey);
    if (cached) { setSvg(cached); setLoading(false); return; }

    let cancelled = false;
    setLoading(true);
    setError(false);

    const doFetch = (retryCount: number) => {
      apiClient.post('/v2/indigo/render', {
        structure: { value: smiles, type: 'RAW' },
        width: `${renderSize}px`,
        height: `${renderSize}px`,
      }, { responseType: 'text' })
        .then((response) => {
          if (!cancelled) {
            svgCache.set(cacheKey, response.data);
            setSvg(response.data);
            setLoading(false);
          }
        })
        .catch((err: any) => {
          if (cancelled) return;
          // Retry once on 503 (transient Indigo overload) after 500ms
          if (retryCount < 1 && err?.response?.status === 503) {
            setTimeout(() => { if (!cancelled) doFetch(retryCount + 1); }, 500);
            return;
          }
          setError(true);
          setLoading(false);
        });
    };

    doFetch(0);

    return () => { cancelled = true; };
  }, [usePng, smiles, renderSize, cacheKey, visible]);

  if ((!smiles && !moleculeId) || error) {
    return (
      <div
        ref={containerRef}
        className={className}
        onClick={onClick}
        style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 10, opacity: 0.4 }}
        title={(smiles || moleculeId) ? 'Structure unavailable' : 'No structure'}
      >
        <svg width={Math.min(size * 0.4, 20)} height={Math.min(size * 0.4, 20)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 3L20 7.5V16.5L12 21L4 16.5V7.5L12 3Z" />
          <path d="M12 12L20 7.5M12 12L4 7.5M12 12V21" />
        </svg>
      </div>
    );
  }

  // Show PNG if available
  if (pngUrl) {
    return (
      <div
        ref={containerRef}
        className={className}
        onClick={onClick}
        style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
        <img src={pngUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      </div>
    );
  }

  // Show SVG if available
  if (svg) {
    return (
      <div
        ref={containerRef}
        className={`${className || ''} [&>svg]:max-w-full [&>svg]:max-h-full`.trim()}
        onClick={onClick}
        style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    );
  }

  if (loading || !visible) {
    return (
      <div
        ref={containerRef}
        className={className}
        onClick={onClick}
        style={{ width: size, height: size, background: 'rgba(241,245,249,0.3)', borderRadius: 4 }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={onClick}
      style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 10 }}
    >
      —
    </div>
  );
};

export default StructureImage;
